import { createRouter, createWebHistory } from 'vue-router';
import { Notify } from 'quasar';
import { useAuthStore, PERMISSION_LEVELS as PERMISSION } from '../stores/auth';

const routes = [
  {
    path: '/',
    redirect: '/dashboard',
  },
  {
    path: '/dashboard',
    component: () => import('../views/Dashboard.vue'),
    name: 'Dashboard',
    meta: {
      title: 'Dashboard - Bar-Tech Portal',
      requiresAuth: true,
    },
  },
  {
    path: '/test-dashboard',
    component: () => import('../views/TestDashboard.vue'),
    name: 'TestDashboard',
    meta: {
      title: 'Test Dashboard - Bar-Tech Portal',
      requiresAuth: true,
      requiresDev: true,  // Only available in development environment
    },
  },
  {
    path: '/products',
    component: () => import('../views/Products.vue'),
    name: 'Products',
    meta: {
      title: 'Products - Bar-Tech Portal',
      requiresAuth: true,
    },
  },
  {
    path: '/quotes',
    component: () => import('../views/Quotes.vue'),
    name: 'Quotes',
    meta: {
      title: 'Quotes - Bar-Tech Portal',
      requiresAuth: true,
      requiredPermission: { module: 'quotes', level: PERMISSION.VIEW },
    },
  },
  {
    path: '/quotes/:id',
    component: () => import('../views/QuoteDetail.vue'),
    name: 'QuoteDetail',
    meta: {
      title: 'Quote Detail - Bar-Tech Portal',
      requiresAuth: true,
      requiredPermission: { module: 'quotes', level: PERMISSION.VIEW },
    },
  },
  {
    path: '/quotes/:id/edit',
    component: () => import('../views/QuoteEdit.vue'),
    name: 'QuoteEdit',
    meta: {
      title: 'Edit Quote - Bar-Tech Portal',
      requiresAuth: true,
      requiredPermission: { module: 'quotes', level: PERMISSION.EDIT },
    },
  },
  {
    path: '/quotes/:quoteId/brick-schema',
    component: () => import('../views/QuoteBrickSchema.vue'),
    name: 'QuoteBrickSchema',
    meta: {
      title: 'Quote Equipment - Bar-Tech Portal',
      requiresAuth: true,
      requiredPermission: { module: 'quotes', level: PERMISSION.VIEW },
    },
  },
  {
    path: '/quotes/new',
    component: () => import('../views/QuoteNew.vue'),
    name: 'QuoteNew',
    meta: {
      title: 'New Quote - Bar-Tech Portal',
      requiresAuth: true,
      requiredPermission: { module: 'quotes', level: PERMISSION.CREATE },
    },
    beforeEnter: (to, from, next) => {
      // Phase 3: Quotes must be created within a tender context
      if (!to.query.tenderId) {
        // No tender ID provided, redirect to tenders list
        next({
          name: 'Tenders',
          query: {
            message: 'Please select a tender first to create a quote',
            messageType: 'info',
          },
        });
      } else {
        next();
      }
    },
  },
  {
    path: '/tenders/:tenderId/quotes/new',
    component: () => import('../views/QuoteNew.vue'),
    name: 'TenderQuoteNew',
    meta: {
      title: 'New Quote - Bar-Tech Portal',
      requiresAuth: true,
      requiredPermission: { module: 'quotes', level: PERMISSION.CREATE },
    },
    props: (route) => ({ tenderId: route.params.tenderId }),
  },
  // Organization routes (unified customer/contractor/builder/consultant/competitor management)
  {
    path: '/organizations',
    component: () => import('../views/Organizations.vue'),
    name: 'Organizations',
    meta: {
      title: 'Organizations - Bar-Tech Portal',
      requiresAuth: true,
      requiredPermission: { module: 'customers', level: PERMISSION.VIEW },
    },
  },
  {
    path: '/organizations/new',
    component: () => import('../views/OrganizationEdit.vue'),
    name: 'OrganizationNew',
    meta: {
      title: 'New Organization - Bar-Tech Portal',
      requiresAuth: true,
      requiredPermission: { module: 'customers', level: PERMISSION.CREATE },
    },
  },
  {
    path: '/organizations/:id',
    component: () => import('../views/OrganizationDetail.vue'),
    name: 'OrganizationDetail',
    meta: {
      title: 'Organization Detail - Bar-Tech Portal',
      requiresAuth: true,
      requiredPermission: { module: 'customers', level: PERMISSION.VIEW },
    },
  },
  {
    path: '/organizations/:id/edit',
    component: () => import('../views/OrganizationEdit.vue'),
    name: 'OrganizationEdit',
    meta: {
      title: 'Edit Organization - Bar-Tech Portal',
      requiresAuth: true,
      requiredPermission: { module: 'customers', level: PERMISSION.EDIT },
    },
  },
  // Legacy customer routes - redirect to organizations with type filter
  {
    path: '/customers',
    redirect: '/organizations?type=customer',
  },
  {
    path: '/customers/:id',
    redirect: (to) => `/organizations/${to.params.id}`,
  },
  {
    path: '/customers/:id/edit',
    redirect: (to) => `/organizations/${to.params.id}/edit`,
  },
  {
    path: '/suppliers',
    component: () => import('../views/Suppliers.vue'),
    name: 'Suppliers',
    meta: {
      title: 'Suppliers - Bar-Tech Portal',
      requiresAuth: true,
      requiredPermission: { module: 'customers', level: PERMISSION.VIEW },
    },
  },
  {
    path: '/users',
    component: () => import('../views/Users.vue'),
    name: 'Users',
    meta: {
      title: 'Users - Bar-Tech Portal',
      requiresAuth: true,
      requiredPermission: { module: 'admin', level: PERMISSION.EDIT },
    },
  },
  // Login is now handled by Laravel blade template, not Vue
  // Removed Vue login route to prevent conflicts
  {
    path: '/sites',
    component: () => import('../views/Sites.vue'),
    name: 'Sites',
    meta: {
      title: 'Sites - Bar-Tech Portal',
      requiresAuth: true,
      requiredPermission: { module: 'sites', level: PERMISSION.VIEW },
    },
  },
  {
    path: '/sites/:id/brick-schema',
    component: () => import('../views/SiteBrickSchema.vue'),
    name: 'SiteBrickSchema',
    meta: {
      title: 'Site BRICK Schema - Bar-Tech Portal',
      requiresAuth: true,
      requiredPermission: { module: 'sites', level: PERMISSION.VIEW },
    },
  },
  {
    path: '/test-airflow',
    component: () => import('../pages/TestAirflow.vue'),
    name: 'TestAirflow',
    meta: {
      title: 'Test Airflow - Bar-Tech Portal',
      requiresAuth: true,
      requiresDev: true,  // Only available in development environment
    },
  },
  {
    path: '/admin',
    component: () => import('../views/Users.vue'),
    name: 'Admin',
    meta: {
      title: 'User Management - Bar-Tech Portal',
      requiresAuth: true,
      requiredPermission: { module: 'admin', level: PERMISSION.VIEW },
    },
  },
  {
    path: '/tenders',
    component: () => import('../views/Tenders.vue'),
    name: 'Tenders',
    meta: {
      title: 'Tenders - Bar-Tech Portal',
      requiresAuth: true,
      requiredPermission: { module: 'quotes', level: PERMISSION.VIEW },
    },
  },
  {
    path: '/tenders/:id',
    component: () => import('../components/tenders/TenderDetail.vue'),
    name: 'TenderDetail',
    meta: {
      title: 'Tender Detail - Bar-Tech Portal',
      requiresAuth: true,
      requiredPermission: { module: 'quotes', level: PERMISSION.VIEW },
    },
  },
  {
    path: '/projects',
    component: () => import('../pages/Projects/ProjectList.vue'),
    name: 'Projects',
    meta: {
      title: 'Projects - Bar-Tech Portal',
      requiresAuth: true,
      requiredPermission: { module: 'projects', level: PERMISSION.VIEW },
    },
  },
  {
    path: '/projects/:id',
    component: () => import('../pages/Projects/ProjectDetail.vue'),
    name: 'ProjectDetail',
    meta: {
      title: 'Project Detail - Bar-Tech Portal',
      requiresAuth: true,
      requiredPermission: { module: 'projects', level: PERMISSION.VIEW },
    },
  },
  {
    path: '/timesheets',
    component: () => import('../pages/Timesheets/TimesheetList.vue'),
    name: 'timesheets',
    meta: {
      title: 'Timesheets - Bar-Tech Portal',
      requiresAuth: true,
      requiredPermission: { module: 'projects', level: PERMISSION.VIEW },
    },
  },
  {
    path: '/timesheets/:id',
    component: () => import('../pages/Timesheets/TimesheetDetail.vue'),
    name: 'timesheet-detail',
    meta: {
      title: 'Timesheet Detail - Bar-Tech Portal',
      requiresAuth: true,
      requiredPermission: { module: 'projects', level: PERMISSION.VIEW },
    },
  },
  {
    path: '/admin/timesheets',
    component: () => import('../pages/Admin/TimesheetAdminDashboard.vue'),
    name: 'admin-timesheets',
    meta: {
      title: 'Timesheet Admin Dashboard - Bar-Tech Portal',
      requiresAuth: true,
      requiredPermission: { module: 'admin', level: PERMISSION.EDIT },
    },
  },
  {
    path: '/admin/roles',
    component: () => import('../pages/Admin/RolesPage.vue'),
    name: 'admin-roles',
    meta: {
      title: 'Roles Management - Bar-Tech Portal',
      requiresAuth: true,
      requiredPermission: { module: 'admin', level: PERMISSION.FULL },
    },
  },
  {
    path: '/admin/audit-logs',
    component: () => import('../pages/Admin/AuditLogsPage.vue'),
    name: 'admin-audit-logs',
    meta: {
      title: 'Audit Logs - Bar-Tech Portal',
      requiresAuth: true,
      requiredPermission: { module: 'admin', level: PERMISSION.VIEW },
    },
  },
  // Tender Configuration Pages
  {
    path: '/tender-config/inclusions',
    component: () => import('../pages/TenderConfig/GeneralInclusionsPage.vue'),
    name: 'tender-config-inclusions',
    meta: {
      title: 'General Inclusions - Bar-Tech Portal',
      requiresAuth: true,
      requiredPermission: { module: 'quotes', level: PERMISSION.EDIT },
    },
  },
  {
    path: '/tender-config/qualifications',
    component: () => import('../pages/TenderConfig/ScopeQualificationsPage.vue'),
    name: 'tender-config-qualifications',
    meta: {
      title: 'Scope Qualifications - Bar-Tech Portal',
      requiresAuth: true,
      requiredPermission: { module: 'quotes', level: PERMISSION.EDIT },
    },
  },
  {
    path: '/tender-config/branding',
    component: () => import('../pages/TenderConfig/BrandingPage.vue'),
    name: 'tender-config-branding',
    meta: {
      title: 'Tender Branding - Bar-Tech Portal',
      requiresAuth: true,
      requiredPermission: { module: 'quotes', level: PERMISSION.EDIT },
    },
  },
  {
    path: '/tender-config/supply-exclusions',
    component: () => import('../components/admin/SupplyExclusionRulesManager.vue'),
    name: 'tender-config-supply-exclusions',
    meta: {
      title: 'Supply Exclusion Rules - Bar-Tech Portal',
      requiresAuth: true,
      requiredPermission: { module: 'quotes', level: PERMISSION.EDIT },
    },
  },
  {
    path: '/profile',
    component: () => import('../views/Profile.vue'),
    name: 'Profile',
    meta: {
      title: 'Profile - Bar-Tech Portal',
      requiresAuth: true,
    },
  },
  {
    path: '/settings',
    component: () => import('../views/SettingsView.vue'),
    name: 'Settings',
    meta: {
      title: 'Settings - Bar-Tech Portal',
      requiresAuth: true,
      // Settings hub is accessible if user has any of: quotes:3, templates:1, or admin:1
      permissionCheck: (authStore) => {
        return authStore.hasPermission('quotes', 3) ||
          authStore.hasPermission('templates', 1) ||
          authStore.hasPermission('admin', 1);
      },
    },
  },
  {
    path: '/schema-builder-site',
    component: () => import('../views/SchemaBuilderSite.vue'),
    name: 'SchemaBuilderSite',
    meta: {
      title: 'Schema Builder',
      requiresAuth: true,
      requiredPermission: { module: 'sites', level: PERMISSION.VIEW },
    },
  },
  {
    path: '/schema-builder-test',
    component: () => import('../views/SchemaBuilderTest.vue'),
    name: 'SchemaBuilderTest',
    meta: {
      title: 'Schema Builder Test',
      requiresAuth: true,
      requiresDev: true,  // Only available in development environment
    },
  },
  {
    path: '/brick-tree-test',
    component: () => import('../views/BrickTreeSelectorTest.vue'),
    name: 'BrickTreeTest',
    meta: {
      title: 'BRICK Tree Selector Test',
      requiresAuth: true,
      requiresDev: true,  // Only available in development environment
    },
  },
  {
    path: '/api-test',
    component: () => import('../views/APITest.vue'),
    name: 'APITest',
    meta: {
      title: 'API Test - Bar-Tech Portal',
      requiresAuth: true,
      requiresDev: true,  // Only available in development environment
    },
  },
  {
    path: '/electrical-comparison',
    component: () => import('../pages/ElectricalComparison.vue'),
    name: 'ElectricalComparison',
    meta: {
      title: 'Electrical Hierarchy Comparison - Bar-Tech Portal',
      requiresAuth: true,
      requiredPermission: { module: 'sites', level: PERMISSION.VIEW },
    },
  },
  {
    path: '/test-electrical-hierarchy',
    component: () => import('../pages/TestElectricalHierarchy.vue'),
    name: 'TestElectricalHierarchy',
    meta: {
      title: 'Test Electrical Hierarchy - Bar-Tech Portal',
      requiresAuth: true,
      requiresDev: true,  // Only available in development environment
    },
  },
  {
    path: '/device-parts',
    component: () => import('../views/DevicePartsManager.vue'),
    name: 'DevicePartsManager',
    meta: {
      title: 'Device Parts Registry - Bar-Tech Portal',
      requiresAuth: true,
      requiredPermission: { module: 'templates', level: PERMISSION.VIEW },
    },
  },
  {
    path: '/templates/controllers',
    component: () => import('../views/ControllerTemplateManager.vue'),
    name: 'ControllerTemplateManager',
    meta: {
      title: 'Controller Templates - Bar-Tech Portal',
      requiresAuth: true,
      requiredPermission: { module: 'templates', level: PERMISSION.VIEW },
    },
  },
  {
    path: '/templates/devices',
    component: () => import('../views/DeviceTemplateManager.vue'),
    name: 'DeviceTemplateManager',
    meta: {
      title: 'Device Templates - Bar-Tech Portal',
      requiresAuth: true,
      requiredPermission: { module: 'templates', level: PERMISSION.VIEW },
    },
  },
  {
    path: '/templates/equipment',
    component: () => import('../views/EquipmentTemplateManager.vue'),
    name: 'EquipmentTemplateManager',
    meta: {
      title: 'Equipment Templates - Bar-Tech Portal',
      requiresAuth: true,
      requiredPermission: { module: 'templates', level: PERMISSION.VIEW },
    },
  },
  {
    path: '/templates/compound',
    component: () => import('../views/CompoundTemplateManager.vue'),
    name: 'CompoundTemplateManager',
    meta: {
      title: 'Compound Templates - Bar-Tech Portal',
      requiresAuth: true,
      requiredPermission: { module: 'templates', level: PERMISSION.VIEW },
    },
  },
  // User Manual routes
  {
    path: '/manual',
    component: () => import('../views/Manual.vue'),
    name: 'Manual',
    meta: {
      title: 'User Manual - Bar-Tech Portal',
      requiresAuth: true,
    },
    children: [
      {
        path: '',
        redirect: '/manual/getting-started/navigation',
      },
      {
        path: ':section',
        component: () => import('../views/Manual.vue'),
        name: 'ManualSection',
        meta: {
          title: 'User Manual - Bar-Tech Portal',
          requiresAuth: true,
        },
      },
      {
        path: ':section/:page',
        component: () => import('../views/Manual.vue'),
        name: 'ManualPage',
        meta: {
          title: 'User Manual - Bar-Tech Portal',
          requiresAuth: true,
        },
      },
    ],
  },
  // Email timesheet page (no auth required - token-based)
  {
    path: '/timesheet/email/:token',
    component: () => import('../pages/Timesheets/TimesheetEmailPage.vue'),
    name: 'timesheet-email',
    meta: {
      title: 'Timesheet Entry - Bar-Tech Portal',
      requiresAuth: false,
      isEmailPage: true,
    },
  },
  // Backward compatibility redirects for old route names
  {
    path: '/Price-List',
    redirect: '/products',
  },
  {
    path: '/price-list',
    redirect: '/products',
  },
  {
    path: '/Customers',
    redirect: '/organizations?type=customer',
  },
  // Login redirects removed - handled by Laravel
  {
    path: '/Sites',
    redirect: '/sites',
  },
  {
    path: '/Admin',
    redirect: '/admin',
  },
  {
    path: '/Tenders',
    redirect: '/tenders',
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to, _from, next) => {
  document.title = to.meta.title || 'Bar-Tech Portal';

  const authStore = useAuthStore();
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  const requiresDev = to.matched.some((record) => record.meta.requiresDev);

  // Get required permission from the matched route (use closest child route's permission)
  const requiredPermission = to.matched
    .slice()
    .reverse()
    .find((record) => record.meta.requiredPermission)?.meta.requiredPermission;

  // Block test/development pages in production
  // import.meta.env.DEV is true in dev mode (npm run dev), false in production builds
  if (requiresDev && !import.meta.env.DEV) {
    Notify.create({
      type: 'warning',
      message: 'This page is only available in development mode.',
      timeout: 5000,
    });
    next({ name: 'Dashboard' });
    return;
  }

  // Skip auth checks for email pages (token-based auth)
  if (to.meta.isEmailPage) {
    next();
    return;
  }

  // Check if we're already on the login page (actual browser URL)
  const currentPath = window.location.pathname;
  if (currentPath === '/login') {
    // Cancel the navigation since we're on the login page (Laravel blade template)
    // This prevents Vue Router from trying to navigate while on login page
    next(false);
    return;
  }

  // Verify auth if token exists but user or permissions not loaded
  if (authStore.token && (!authStore.user || !authStore.user.effective_permissions)) {
    try {
      await authStore.verifyAuth();
    } catch (error) {
      // Clear invalid token
      authStore.clearAuth();
    }
  }

  // Check authentication first
  if (requiresAuth && !authStore.isAuthenticated) {
    // Store the intended destination
    const redirectPath = to.fullPath !== '/' ? to.fullPath : '/dashboard';
    // Redirect to Laravel blade login page
    window.location.href = '/login?redirect=' + encodeURIComponent(redirectPath);
    // Cancel the navigation since we're redirecting with window.location
    next(false);
    return;
  }

  // Check custom permission function if provided
  const permissionCheck = to.matched
    .slice()
    .reverse()
    .find((record) => record.meta.permissionCheck)?.meta.permissionCheck;

  if (permissionCheck && authStore.isAuthenticated) {
    if (!permissionCheck(authStore)) {
      Notify.create({
        type: 'negative',
        message: 'You do not have permission to access this page. Redirecting to dashboard.',
        timeout: 5000,
      });
      next({ name: 'Dashboard' });
      return;
    }
  }

  // Check standard granular permission if required
  if (requiredPermission && authStore.isAuthenticated) {
    const { module, level } = requiredPermission;
    const hasAccess = authStore.hasPermission(module, level);

    if (!hasAccess) {
      const levelNames = ['None', 'View', 'Create', 'Edit', 'Delete', 'Full'];
      const levelName = levelNames[level] || 'access';

      Notify.create({
        type: 'negative',
        message: `You do not have ${levelName.toLowerCase()} permission for ${module}. Redirecting to dashboard.`,
        timeout: 5000,
      });
      next({ name: 'Dashboard' });
      return;
    }
  }

  next();
});

export default router;